import { type ChangeEvent, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "vega-icons";

import { useStore } from "@nanostores/react";
import { $organizationId } from "@vega-api/custom/stores/organisations.store";
import { usePagination } from "@vega-api/custom/utils/usePagination";
import { useCurrentUserRolePermission } from "@vega-api/extended/auth/permissions/useCurrentUserRolePermission";
import type { GetManageableUsersForLimitedPartner200 } from "@vega-api/generated/types/getManageableUsersForLimitedPartner200";
import { UserStatus } from "@vega-api/generated/types/userStatus";
import {
  getGetManageableUsersForLimitedPartnerQueryKey,
  useGetManageableUsersForLimitedPartner,
} from "@vega-api/generated/user-management-v1/user-management-v1";
import { SecondaryButton } from "@vega-core/components/Buttons/SecondaryButton";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { SearchInput } from "@vega-core/components/Form/Controls/Input/SearchInput/SearchInput";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { m } from "@vega-core/paraglide/messages";
import { convertToTitleCase } from "@vega-core/utils/formatString";
import { useDebounce } from "@vega-core/utils/useDebounce";

import { SettingsSectionLayout } from "../../components/SettingsSectionLayout";

import { InviteUserModal } from "./modals/InviteUserModal/InviteUserModal";
import { UsersTable } from "./UsersTable";

const ALL_STATUSES = "ALL";

const EMPTY_USERS_RESPONSE: GetManageableUsersForLimitedPartner200 = {
  data: [],
  meta: undefined,
  errors: null,
};

const statusOptions = [
  { label: "All", value: ALL_STATUSES },
  ...Object.values(UserStatus).map((status) => ({
    label: convertToTitleCase(status),
    value: status,
  })),
];

export const UserManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>(ALL_STATUSES);
  const debouncedSearchQuery = useDebounce(searchQuery.trim(), 500);
  const isInviteModalOpen = useBoolean(false);
  const queryClient = useQueryClient();
  const selectedOrgId = useStore($organizationId);

  const { isLoading: isPermissionsLoading } = useCurrentUserRolePermission();

  const { currentPage, currentPageSize, setCurrentPage, setCurrentPageSize } =
    usePaginationState();

  const statusFilter =
    selectedStatus === ALL_STATUSES
      ? undefined
      : (selectedStatus as UserStatus);

  const { data: usersResponse, isLoading: isUsersLoading } =
    useGetManageableUsersForLimitedPartner(
      {
        page: currentPage,
        pageSize: currentPageSize,
        searchQuery: debouncedSearchQuery || undefined,
        status: statusFilter,
      },
      {
        query: {
          placeholderData: EMPTY_USERS_RESPONSE,
          select: (response) => response,
        },
      },
    );

  const { data: usersData = [], meta } =
    usersResponse as GetManageableUsersForLimitedPartner200;

  const { paginationElement } = usePagination({
    meta,
    currentPage,
    setCurrentPage,
    isLoading: isUsersLoading,
    currentPageSize,
    setCurrentPageSize,
    filterDependencies: [debouncedSearchQuery, selectedStatus, selectedOrgId],
  });

  // Invalidate users query when organization changes
  useEffect(() => {
    if (selectedOrgId) {
      queryClient.invalidateQueries({
        queryKey: getGetManageableUsersForLimitedPartnerQueryKey(),
      });
    }
  }, [selectedOrgId, queryClient]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <InviteUserModal
        onClose={isInviteModalOpen.setFalse}
        open={isInviteModalOpen.value}
      />
      <SettingsSectionLayout bordered className="overflow-hidden p-4">
        <div className="flex flex-col gap-4 p-6">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex flex-1 flex-wrap items-center gap-4">
              <div className="w-64">
                <SearchInput
                  background="bg-background/primary"
                  compact
                  name="userManagementSearch"
                  onChange={handleSearchChange}
                  placeholder={m.placeholders_searchUsers()}
                  value={searchQuery}
                  withBorder
                />
              </div>
              <div className="w-40">
                <ThemedDropdown
                  isClearable={false}
                  name="userManagementStatusFilter"
                  onChange={(option) =>
                    setSelectedStatus(option?.value ?? ALL_STATUSES)
                  }
                  options={statusOptions}
                  placeholder={m.titles_status()}
                  value={selectedStatus}
                />
              </div>
            </div>
            <SecondaryButton
              dataTestId="invite-button"
              fullWidth={false}
              icon={<PlusIcon />}
              iconPosition="right"
              onClick={isInviteModalOpen.setTrue}
            >
              {m.btn_inviteUser()}
            </SecondaryButton>
          </div>
        </div>
        <div className="p-4">
          <UsersTable
            data={usersData}
            isLoading={isPermissionsLoading || isUsersLoading}
            paginationElement={paginationElement}
          />
        </div>
      </SettingsSectionLayout>
    </div>
  );
};
